import { TMProviderInvoiceAPI } from '@business/api/tm_provider_invoice';
import {
  BillsInvoiceSearchParams,
  TMProviderInvoiceDetailedResponse,
  TMProviderInvoiceId,
  TMProviderInvoiceResponse,
  TMProviderInvoiceSearchParams,
} from '@business/entities/tm/TMProviderInvoiceResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { ProviderInvoiceFormData } from '@modules/timeMaterials/TimeMaterialOverlay/ProviderInvoiceModal/ProviderInvoiceModal';
import { TMInvoiceScheduleFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/Order/AddEditTMInvoiceScheduleModal/AddEditTMInvoiceScheduleModal';
import { ProviderBillFormData } from '@modules/vendors/PayablesPage/AddEditProviderBill/ProviderBillForm';
import { BillFormData } from '@modules/vendors/PayablesPage/ViewProviderBill/ViewProviderBillModal';
import { switchMap } from 'rxjs/operators';

export class TMProviderInvoiceBloc extends BaseBloc {
  private readonly typeRepo = new Repository<TMProviderInvoiceResponse | TMProviderInvoiceDetailedResponse>({
    getItemId: item => item.id,
  });

  readonly tmInvoiceSchedules$ = this.typeRepo.items$;
  readonly selectTMInvoiceSchedule = this.typeRepo.selectItem;

  onReset() {
    this.typeRepo.reset();
  }

  fetchInvoices = (data?: BillsInvoiceSearchParams) => {
    return TMProviderInvoiceAPI.fetchInvoices(data).pipe(
      this.typeRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_invoice_schedule_error') }),
    );
  };

  fetchInvoice = (id: TMProviderInvoiceId) => {
    return TMProviderInvoiceAPI.fetchInvoice(id).pipe(
      this.typeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_invoice_schedule_error') }),
    );
  };

  createInvoice = (data: ProviderBillFormData) => {
    return TMProviderInvoiceAPI.createInvoice(data).pipe(
      this.typeRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_invoice_schedule'),
      }),
    );
  };

  createInvoices = (data: ProviderInvoiceFormData) => {
    return TMProviderInvoiceAPI.createInvoices(data).pipe(
      this.typeRepo.ops.addMany(items => items),
      handleMessage({
        type: requestMessage('create_invoice_schedule'),
      }),
    );
  };

  updateInvoice = (id: TMProviderInvoiceId, data: TMInvoiceScheduleFormData) => {
    return TMProviderInvoiceAPI.updateInvoice(id, data).pipe(
      this.typeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_invoice_schedule'),
      }),
    );
  };

  deleteInvoice = (id: TMProviderInvoiceId) => {
    return showDeleteConfirmation('Delete Inovice', 'Do you really want to remove this Inovice?').pipe(
      switchMap(() => TMProviderInvoiceAPI.deleteInvoice(id)),
      this.typeRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_invoice_schedule'),
      }),
    );
  };

  syncInvoiceToQBO = (id: TMProviderInvoiceId) => {
    return TMProviderInvoiceAPI.syncInvoiceToQBO(id).pipe(
      switchMap(() => this.fetchInvoice(id)),
      handleMessage({
        type: requestMessage('sync_invoice_qbo'),
      }),
    );
  };

  reSyncInvoiceToSystem = (id: TMProviderInvoiceId) => {
    return TMProviderInvoiceAPI.reSyncInvoiceToSystem(id).pipe(
      switchMap(() => this.fetchInvoice(id)),
      handleMessage({
        type: requestMessage('resync_invoice_qbo'),
      }),
    );
  };

  fetchAllInvoices = () => {
    return TMProviderInvoiceAPI.fetchAllInvoices().pipe(
      this.typeRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_invoice_schedule_error') }),
    );
  };

  updateProviderItems = (id: TMProviderInvoiceId, data: BillFormData) => {
    return TMProviderInvoiceAPI.updateProviderItems(id, data).pipe(
      // switchMap(() => this.fetchInvoice(id)),
      this.typeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_provider_items'),
      }),
    );
  };
}
